import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, X, CheckCircle2, ListTodo, Gift, Users, Bell, Link2 } from 'lucide-react';

export default function WelcomeCarousel({ onComplete }) {
  const { i18n } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const lang = i18n.language || 'es';

  const slides = {
    es: [
      {
        icon: ListTodo,
        color: '#FF8BA7',
        title: '¡Bienvenido a Motivator!',
        description: 'Convierte tus tareas en un juego. Crea tareas, asigna puntos y completa objetivos para ganar recompensas.',
        tip: 'Cada tarea completada suma puntos a tu lista.'
      },
      {
        icon: Link2,
        color: '#ADE498',
        title: 'Puntos por Lista',
        description: 'Cada lista de tareas tiene sus propios puntos. Los premios están vinculados a una lista específica.',
        tip: 'Solo puedes canjear premios con los puntos de su lista asociada.'
      },
      {
        icon: Gift,
        color: '#FFC247',
        title: 'Sistema de Recompensas',
        description: 'Crea tus propios premios y canjéalos cuando tengas suficientes puntos. ¡Motívate con recompensas personalizadas!',
        tip: 'Los premios pueden ser desde un café hasta un día libre.'
      },
      {
        icon: Users,
        color: '#3B82F6',
        title: 'Colabora en Equipo',
        description: 'Comparte tus listas con familia, amigos o compañeros. Todos acumulan puntos en la misma lista.',
        tip: 'Invita usuarios por email para compartir listas.'
      },
      {
        icon: Bell,
        color: '#FF8BA7',
        title: 'Mantente al Día',
        description: 'Activa las notificaciones push para recibir alertas cuando alguien complete una tarea o canjee un premio.',
        tip: 'Las notificaciones funcionan incluso con la app cerrada.'
      }
    ],
    en: [
      {
        icon: ListTodo,
        color: '#FF8BA7',
        title: 'Welcome to Motivator!',
        description: 'Turn your tasks into a game. Create tasks, assign points and complete goals to earn rewards.',
        tip: 'Each completed task adds points to your list.'
      },
      {
        icon: Link2,
        color: '#ADE498',
        title: 'Points per List',
        description: 'Each task list has its own points. Rewards are linked to a specific list.',
        tip: 'You can only redeem rewards with points from their associated list.'
      },
      {
        icon: Gift,
        color: '#FFC247',
        title: 'Rewards System',
        description: 'Create your own rewards and redeem them when you have enough points. Motivate yourself with personalized rewards!',
        tip: 'Rewards can be anything from a coffee to a day off.'
      },
      {
        icon: Users,
        color: '#3B82F6',
        title: 'Team Collaboration',
        description: 'Share your lists with family, friends or colleagues. Everyone accumulates points in the same list.',
        tip: 'Invite users by email to share lists.'
      },
      {
        icon: Bell,
        color: '#FF8BA7',
        title: 'Stay Updated',
        description: 'Enable push notifications to get alerts when someone completes a task or redeems a reward.',
        tip: 'Notifications work even with the app closed.'
      }
    ],
    gl: [
      {
        icon: ListTodo,
        color: '#FF8BA7',
        title: 'Benvido a Motivator!',
        description: 'Converte as túas tarefas nun xogo. Crea tarefas, asigna puntos e completa obxectivos para gañar recompensas.',
        tip: 'Cada tarefa completada suma puntos á túa lista.'
      },
      {
        icon: Link2,
        color: '#ADE498',
        title: 'Puntos por Lista',
        description: 'Cada lista de tarefas ten os seus propios puntos. Os premios están vinculados a unha lista específica.',
        tip: 'Só podes canjear premios cos puntos da súa lista asociada.'
      },
      {
        icon: Gift,
        color: '#FFC247',
        title: 'Sistema de Recompensas',
        description: 'Crea os teus propios premios e canjéaos cando teñas suficientes puntos. Motívate con recompensas personalizadas!',
        tip: 'Os premios poden ser desde un café ata un día libre.'
      },
      {
        icon: Users,
        color: '#3B82F6',
        title: 'Colabora en Equipo',
        description: 'Comparte as túas listas con familia, amigos ou compañeiros. Todos acumulan puntos na mesma lista.',
        tip: 'Invita usuarios por email para compartir listas.'
      },
      {
        icon: Bell,
        color: '#FF8BA7',
        title: 'Mantente ao Día',
        description: 'Activa as notificacións push para recibir alertas cando alguén complete unha tarefa ou canjee un premio.',
        tip: 'As notificacións funcionan incluso coa app pechada.'
      }
    ],
    ca: [
      {
        icon: ListTodo,
        color: '#FF8BA7',
        title: 'Benvingut a Motivator!',
        description: 'Converteix les teves tasques en un joc. Crea tasques, assigna punts i completa objectius per guanyar recompenses.',
        tip: 'Cada tasca completada suma punts a la teva llista.'
      },
      {
        icon: Link2,
        color: '#ADE498',
        title: 'Punts per Llista',
        description: 'Cada llista de tasques té els seus propis punts. Els premis estan vinculats a una llista específica.',
        tip: 'Només pots bescanviar premis amb els punts de la seva llista associada.'
      },
      {
        icon: Gift,
        color: '#FFC247',
        title: 'Sistema de Recompenses',
        description: "Crea els teus propis premis i canvia'ls quan tinguis prou punts. Motiva't amb recompenses personalitzades!",
        tip: "Els premis poden ser des d'un cafè fins a un dia lliure."
      },
      {
        icon: Users,
        color: '#3B82F6',
        title: "Col·labora en Equip",
        description: 'Comparteix les teves llistes amb família, amics o companys. Tots acumulen punts a la mateixa llista.',
        tip: 'Convida usuaris per email per compartir llistes.'
      },
      {
        icon: Bell,
        color: '#FF8BA7',
        title: "Mantén-te al Dia",
        description: 'Activa les notificacions push per rebre alertes quan algú completi una tasca o canviï un premi.',
        tip: "Les notificacions funcionen fins i tot amb l'app tancada."
      }
    ],
    zh: [
      {
        icon: ListTodo,
        color: '#FF8BA7',
        title: '欢迎使用 Motivator！',
        description: '将您的任务变成游戏。创建任务，分配积分，完成目标以获得奖励。',
        tip: '每完成一项任务都会为您的列表增加积分。'
      },
      {
        icon: Link2,
        color: '#ADE498',
        title: '每个列表的积分',
        description: '每个任务列表都有自己的积分。奖励与特定列表相关联。',
        tip: '您只能用关联列表的积分兑换奖励。'
      },
      {
        icon: Gift,
        color: '#FFC247',
        title: '奖励系统',
        description: '创建您自己的奖励，当积分足够时兑换它们。用个性化奖励激励自己！',
        tip: '奖励可以是从一杯咖啡到休息一天的任何东西。'
      },
      {
        icon: Users,
        color: '#3B82F6',
        title: '团队协作',
        description: '与家人、朋友或同事分享您的列表。每个人都在同一个列表中积累积分。',
        tip: '通过电子邮件邀请用户共享列表。'
      },
      {
        icon: Bell,
        color: '#FF8BA7',
        title: '保持更新',
        description: '启用推送通知，当有人完成任务或兑换奖励时收到提醒。',
        tip: '即使应用关闭，通知也会工作。'
      }
    ]
  };

  const currentSlides = slides[lang] || slides.es;
  const slide = currentSlides[currentSlide];

  const buttonText = {
    es: { skip: 'Saltar', next: 'Siguiente', start: '¡Continuar!' },
    en: { skip: 'Skip', next: 'Next', start: 'Continue!' },
    gl: { skip: 'Saltar', next: 'Seguinte', start: '¡Continuar!' },
    ca: { skip: 'Saltar', next: 'Següent', start: 'Continuar!' },
    zh: { skip: '跳过', next: '下一步', start: '继续！' }
  };
  const btn = buttonText[lang] || buttonText.es;

  const nextSlide = () => {
    if (currentSlide < currentSlides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      onComplete();
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      data-testid="welcome-carousel"
    >
      <div className="bg-white dark:bg-zinc-800 border-2 border-[#18181B] dark:border-zinc-600 rounded-2xl hard-shadow w-full max-w-md overflow-hidden">
        {/* Header with skip button */}
        <div className="flex justify-end p-4 pb-0">
          <button
            onClick={onComplete}
            className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 text-sm font-medium flex items-center gap-1"
            data-testid="skip-carousel-btn"
          >
            {btn.skip}
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Slide Content */}
        <div className="px-8 pb-8 pt-4 text-center">
          {/* Icon */}
          <div 
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-[#18181B] dark:border-zinc-600"
            style={{ backgroundColor: slide.color }}
          >
            <slide.icon className="w-10 h-10 text-[#18181B]" />
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold mb-3 text-zinc-900 dark:text-white">{slide.title}</h2>

          {/* Description */}
          <p className="text-zinc-600 dark:text-zinc-300 mb-4 leading-relaxed">{slide.description}</p>

          {/* Tip */}
          <div className="bg-zinc-50 dark:bg-zinc-700 rounded-xl p-3 text-sm text-zinc-500 dark:text-zinc-300 flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-[#ADE498] flex-shrink-0 mt-0.5" />
            <span>{slide.tip}</span>
          </div>

          {/* Dots indicator */}
          <div className="flex justify-center gap-2 mt-6">
            {currentSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentSlide 
                    ? 'bg-[#18181B] dark:bg-white w-6' 
                    : 'bg-zinc-300 dark:bg-zinc-600 hover:bg-zinc-400 dark:hover:bg-zinc-500'
                }`}
              />
            ))}
          </div>

          {/* Navigation Buttons */}
          <div className="flex gap-3 mt-6">
            {currentSlide > 0 && (
              <button
                onClick={prevSlide}
                className="flex-1 rounded-full border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white py-3 font-bold hover:bg-zinc-50 dark:hover:bg-zinc-600 transition-all flex items-center justify-center gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={nextSlide}
              className="flex-1 rounded-full border-2 border-[#18181B] bg-[#18181B] text-white py-3 font-bold hover:bg-[#333] transition-all flex items-center justify-center gap-2"
              data-testid="next-carousel-btn"
            >
              {currentSlide === currentSlides.length - 1 ? btn.start : btn.next}
              {currentSlide < currentSlides.length - 1 && <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
